'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import { DestinationLink } from '../blocks/DestinationLink'

import Link from 'next/link'

import * as React from 'react'
import { Bell, GitCompareArrows, Heart, Menu, Search, BadgePercent, Tags, Tag } from '@/components/homepage-october/lucide'

import { Button } from '@/components/homepage-october/ui/button'
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/homepage-october/ui/input-group'
import { Kbd } from '@/components/ui/kbd'
import { DesktopNavigation } from '../blocks/SiteNavigation'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { Img } from '../blocks/Img'
import { Container, LiveDot } from '../blocks/layout'
import { Marquee } from '../blocks/Rail'
import { useDragScroll } from '../hooks'
import { COMPARE_MAX, useShop } from '../state'
import type { Model } from '../types'

/* ------------------------------------------------------------------ top bar: live prices + follow */
export function TopBar({ m }: { m: Model['util'] }) {
  return (
    <div role="region" aria-label={m.aria} className={compactClassName("border-b border-neutral-200 bg-neutral-50 text-[13px]")}>
      <Container className="flex h-10 items-center gap-3">
        <p className={compactClassName("flex shrink-0 items-center gap-2 font-semibold text-neutral-900")}>
          <LiveDot />
          {m.live}
        </p>
        <Separator orientation="vertical" className="h-4!" />
        <Marquee still="scroll" duration={340} className="min-w-0 flex-1 [mask-image:linear-gradient(90deg,transparent,black_4%,black_96%,transparent)]">
          {m.ticker.map((t) => (
            <DestinationLink key={t.href} href={t.href} className="mr-6 inline-flex min-h-7 items-center gap-1.5 whitespace-nowrap text-neutral-700 hover:text-[#0454c0]">
              <span className={compactClassName("font-semibold text-[#067647]")}>{t.drop}</span>
              <b className={compactClassName("font-semibold text-neutral-900")}>{t.brand}</b>
              <span className={compactClassName("text-neutral-700")}>{t.title}</span>
              <span className={compactClassName("tabular font-semibold text-neutral-950")}>{t.price}</span>

            </DestinationLink>
          ))}
        </Marquee>
        <nav aria-label="Information and local help" className={compactClassName("v3-top-utility")}>
          <Link prefetch={false} href="/rebates">Local rebates</Link>
          <Link prefetch={false} href="/data">Price data</Link>
          <Link prefetch={false} href="/about-us/methodology">How we compare</Link>
        </nav>
      </Container>
    </div>
  )
}

/* ------------------------------------------------------------------ skip link (the first thing Tab reaches) */
/** FRIDGE-1576: `.v3-skip-link` (homepage.css) is .sr-only at rest and the fixed top-left pill under keyboard focus, the
 *  FRIDGE-138 `.skip-link` pattern. The `sr-only focus:not-sr-only …` stack lost to the unlayered .sr-only and stayed 1 by 1 px. */
export function SkipLink({ m }: { m: Model['header']['skip'] }) {
  return (
    <DestinationLink href={m.href} className="v3-skip-link">
      {m.text}
    </DestinationLink>
  )
}

/* ------------------------------------------------------------------ header */
export function SiteHeader({ m, model }: { m: Model['header']; model: Model }) {
  const shop = useShop()
  React.useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const typing = /input|textarea|select/i.test((e.target as HTMLElement)?.tagName) || (e.target as HTMLElement)?.isContentEditable
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing)) {
        e.preventDefault()
        shop.open('search')
      }
    }
    window.addEventListener('keydown', on)
    return () => window.removeEventListener('keydown', on)
  }, [shop])
  // A shadow once the page has scrolled under the header (an external store, so the server and first paint agree).
  const scrolled = React.useSyncExternalStore((cb) => { window.addEventListener('scroll', cb, { passive: true }); return () => window.removeEventListener('scroll', cb) }, () => window.scrollY > 8, () => false)
  const alerts = model.mbar.links.find((l) => /alert/i.test(l.text))
  return (
    <header className={compactClassName(cn('sticky top-0 z-50 border-b border-neutral-200/80 bg-white/90 backdrop-blur-md transition-shadow supports-[backdrop-filter]:bg-white/80', scrolled && 'shadow-[0_12px_32px_-24px_rgba(16,24,40,0.5)]'))}>
      <Container className="flex h-[58px] items-center gap-1.5 md:gap-3 lg:h-[66px]">
        <Button variant="ghost" size="icon" className="-ml-2 size-11 lg:hidden" aria-label={m.menuBtn} onClick={(e) => shop.open('menu', e.currentTarget)}>
          <Menu aria-hidden="true" className="size-5" />
        </Button>
        <DestinationLink href={m.logo.href} aria-label={m.logo.aria} className="-my-1 flex min-w-0 flex-1 items-center md:flex-none md:shrink-0">
          <span className={compactClassName("sr-only")}>{m.logo.text}</span>
          {/* Narrow screens keep search in its own row so the input stays useful. */}
          <img src={m.logo.img.src} alt="" className={compactClassName("h-12 w-auto max-w-full object-contain sm:h-[52px] lg:h-14")} />
        </DestinationLink>
        {/* A real search box at every width, with a separate row below 1024 px.
            Enter searches fridge.com exactly like the live header; ⌘K opens the quick-links palette. */}
        <form action={model.hero.search.action} method={model.hero.search.method} role="search" aria-label={m.search.aria} className={compactClassName("mx-auto hidden w-full max-w-[1100px] lg:block")}>
          <InputGroup className="h-12 rounded-full! border-neutral-300 bg-white pr-1 pl-2 shadow-xs! has-[[data-slot=input-group-control]:focus-visible]:border-[#0469f0]! has-[[data-slot=input-group-control]:focus-visible]:bg-white">
            <InputGroupAddon><Search aria-hidden="true" className="size-4 text-neutral-500" /></InputGroupAddon>
            <InputGroupInput type="search" name={model.hero.search.name} placeholder={m.search.placeholder} aria-label={m.search.aria} className="text-base! shadow-none! focus-visible:ring-0!" />
            <InputGroupAddon align="inline-end" className="gap-1">
              <InputGroupButton type="button" size="xs" aria-label="⌘K quick links" title="Quick links (⌘K)" onClick={(e) => shop.open('search', e.currentTarget)} className="hidden! rounded-full! lg:inline-flex!">
                <Kbd className="text-neutral-700!">⌘K</Kbd>
              </InputGroupButton>
              <InputGroupButton type="submit" variant="ctaPrimary" className="h-9! rounded-full! px-5! font-semibold">{model.hero.search.button}</InputGroupButton>
            </InputGroupAddon>
          </InputGroup>
        </form>
        <div className={compactClassName("ml-auto flex shrink-0 items-center gap-1 lg:ml-0")}>
          {alerts ? <DestinationLink href={alerts.href} className="v3-header-alert" aria-label="Price alerts"><Bell aria-hidden="true" className="size-5" /><span>Price alerts</span></DestinationLink> : null}
          <span className={compactClassName("hidden md:contents")}>
            <HeaderButton label={m.compare.label} aria={m.compare.aria} count={shop.compare.length} onClick={(e) => shop.open('compare', e.currentTarget)}>
              <GitCompareArrows aria-hidden="true" className="size-5" />
            </HeaderButton>
          </span>
          <HeaderButton label={m.saved.label} aria={m.saved.aria} count={shop.saved.length} onClick={(e) => shop.open('saved', e.currentTarget)}>
            <Heart aria-hidden="true" className="size-5" />
          </HeaderButton>
        </div>
      </Container>
      <Container className="pb-2 lg:hidden">
        <form action={model.hero.search.action} method={model.hero.search.method} role="search" aria-label="Search appliances" className={compactClassName("v3-phone-search")}>
          <Search aria-hidden="true" /><input type="search" name={model.hero.search.name} placeholder="Model, brand or size" aria-label="Search by model, brand or size" /><button type="submit">Search</button>
        </form>
      </Container>
      <div className={compactClassName("hidden border-t border-neutral-200/70 lg:block")}>
        <Container><DesktopNavigation model={model} /></Container>
      </div>
    </header>
  )
}

function HeaderButton({ label, aria, count, onClick, children }: { label: string; aria: string; count: number; onClick: (e: React.MouseEvent<HTMLButtonElement>) => void; children: React.ReactNode }) {
  return (
    <Button variant="ghost" aria-label={count ? `${label} ${count}. ${aria}` : aria} onClick={onClick} className="relative h-11 min-w-11 flex-col gap-0.5! px-2! text-neutral-700 sm:px-3!">
      {children}
      <span className={compactClassName("hidden text-[11px] font-medium leading-none sm:block")}>{label}</span>{' '}
      {count ? (
        <span className={compactClassName("absolute top-1 right-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-[#0469f0] px-1 text-[10px] font-bold leading-[18px] text-white")}>{count}</span>
      ) : null}
    </Button>
  )
}

/* ------------------------------------------------------------------ departments */
export function Departments({ m }: { m: Model['depts'] }) {
  const row = React.useRef<HTMLUListElement>(null)
  const drag = useDragScroll(row)
  return (
    <nav aria-label={m.aria} className={compactClassName("border-b border-neutral-200/70 bg-white")}>
      <Container className="px-0! sm:px-6! lg:px-8!">
        <ul ref={row} {...drag} className={compactClassName("v3-noscrollbar flex gap-1 overflow-x-auto px-4 py-3 sm:px-0 lg:justify-between")}>
          {m.items.map((d) => (
            <li key={d.href} className={compactClassName("shrink-0")}>
              <DestinationLink href={d.href} className="group flex w-[88px] flex-col items-center gap-2 rounded-xl px-1 py-1.5 text-center text-[12.5px] font-medium leading-tight text-neutral-800 hover:text-[#0454c0] md:w-[98px]">
                <span className={compactClassName(cn('flex size-[70px] items-center justify-center overflow-hidden rounded-2xl ring-1 transition group-hover:-translate-y-0.5 group-hover:shadow-md md:size-[76px]', d.hot ? 'bg-[#ff9951] ring-[#ff9951]' : 'bg-[#f3f6fa] ring-black/5'))}>
                  {d.text === 'Brands' ? <Tags aria-hidden="true" className="size-8 text-[#0454c0]" strokeWidth={1.75} /> : d.hot ? <BadgePercent aria-hidden="true" className="size-9 text-neutral-950" strokeWidth={1.75} /> : d.photo || d.img ? <Img src={(d.photo || d.img)!.src} alt="" className="size-full p-1 mix-blend-multiply" /> : <span aria-hidden="true" className={compactClassName("text-xl font-black text-[#1a1a1a]")}>{d.glyph}</span>}
                </span>
                {d.glyph ? d.text.replace(d.glyph, '').trim() : d.text}
              </DestinationLink>
            </li>
          ))}
        </ul>
      </Container>
    </nav>
  )
}

/* ------------------------------------------------------------------ phone bar + Frosty pill + compare tray */
export function MobileBar({ m }: { m: Model['mbar'] }) {
  const shop = useShop()
  const icon = { Deals: Tag, Search, Compare: GitCompareArrows, Alerts: Bell } as Record<string, typeof Tag>
  return (
    <nav data-homepage-bottom-bar aria-label={m.aria} className={compactClassName("fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden")}>
      <ul className={compactClassName("grid grid-cols-4")}>
        {m.links.map((l) => {
          const I = icon[l.text] ?? Tag
          return (
            <li key={l.href}>
              <DestinationLink
                href={l.href}
                onClick={(e) => {
                  if (l.text === 'Search') { e.preventDefault(); shop.open('search', e.currentTarget) }
                  if (l.text === 'Compare') { e.preventDefault(); shop.open('compare', e.currentTarget) }
                }}
                className={cn('relative flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-medium', l.hot ? 'text-[#0454c0]' : 'text-neutral-700')}
              >
                <I aria-hidden="true" data-motion={l.text === 'Alerts' ? '' : undefined} className={cn('size-5', l.text === 'Alerts' && 'v3-bell')} />
                {l.text}
                {l.text === 'Compare' && shop.compare.length ? <><span className={compactClassName("sr-only")}> ({shop.compare.length})</span><span aria-hidden="true" className={compactClassName("absolute top-1.5 left-1/2 ml-1.5 flex size-4 items-center justify-center rounded-full bg-[#0469f0] text-[10px] font-bold text-white")}>{shop.compare.length}</span></> : null}
              </DestinationLink>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

export function CompareTray() {
  const shop = useShop()
  if (!shop.compare.length) return null
  return (
    <div className={compactClassName("fixed inset-x-0 bottom-16 z-40 flex justify-center px-4 md:bottom-6")} data-motion>
      <div className={compactClassName("flex animate-v3-rise items-center gap-3 rounded-full bg-white py-2 pr-2 pl-4 text-sm font-medium text-neutral-900 shadow-2xl ring-1 ring-black/10")}>
        <GitCompareArrows aria-hidden="true" className="size-4 text-[#0469f0]" />
        <span className={compactClassName("tabular")}>{shop.compare.length} of {COMPARE_MAX} selected</span>
        <Button size="sm" variant="ctaPrimary" className="rounded-full!" onClick={(e) => shop.open('compare', e.currentTarget)}>Review</Button>
      </div>
    </div>
  )
}
